/**
 * Selectize Service
 *
 * Thin client for the backend quick-search endpoint that powers
 * autocomplete/typeahead dropdowns:
 *
 *   GET /api/selectize/{resource}?search=<term>
 *
 * The endpoint returns the distinct values already used across the current
 * user's accessible custom exercises (NOT a fixed taxonomy), shaped as
 * `{ value, label }` ready to drop into a selectize control. An empty `search`
 * returns every accessible value, which makes it suitable for showing
 * suggestions on focus.
 *
 * Results are cached in-memory with a short TTL and identical concurrent
 * requests are de-duplicated, so a user repeatedly focusing/blurring a field
 * (or the debounced input firing the same query) does not hammer the server.
 * Call {@link SelectizeService.clearCache} after a mutation that could change
 * the available values (e.g. creating a custom exercise).
 *
 * @module services/selectize
 */

import { apiClient } from './api/client';
import { rethrowTyped } from './api/errors';
import { registerSessionScoped } from '../store/session-scope';
import { syncGetAccountUsername } from '../store/store-sync';
import { dayjs } from '../utils/date';

import type { QueryParams } from '../types/api';
import type {
  SelectizeResource,
  SelectizeResponse,
  SelectizeSuggestion,
  SelectizeSuggestionFor,
} from '../types/domain/selectize.types';

// =============================================================================
// TYPES
// =============================================================================

/** Options accepted by {@link SelectizeService.search}. */
export interface SelectizeSearchOptions<Compact extends boolean = true> {
  /**
   * When `true` (default) the endpoint returns the lean `{ value, label }`
   * shape (sent as `compact=1`). Pass `false` to request the resource's full
   * projection (`compact` is omitted; the API accepts only `true|1|yes`).
   * `workout`, `plan`, `user_goal` and `friend` return extra fields; other
   * resources ignore it.
   */
  compact?: Compact;
}

// =============================================================================
// CONSTANTS
// =============================================================================

/** How long a cached result stays fresh (ms). */
const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;

/** Cap on distinct cache entries before the oldest is evicted. */
const MAX_CACHE_ENTRIES = 50;

// =============================================================================
// SERVICE
// =============================================================================

export class SelectizeService {
  private readonly baseUrl = '/selectize';

  private readonly cacheTtlMs: number;

  /** Resolved results keyed by `${resource}::${c|f}::${normalizedTerm}::${account}`. */
  private readonly cache = new Map<
    string,
    { data: SelectizeSuggestion[]; timestamp: number }
  >();

  /** In-flight requests keyed identically, so duplicates share one promise. */
  private readonly inFlight = new Map<string, Promise<SelectizeSuggestion[]>>();

  constructor(cacheTtlMs: number = DEFAULT_CACHE_TTL_MS) {
    this.cacheTtlMs = cacheTtlMs;
  }

  /**
   * Fetch suggestions for a resource, optionally filtered by a search term.
   *
   * Fresh cached results are returned without a network call; identical
   * in-flight requests share a single promise. Compact and full results are
   * cached independently, so the same resource/term can be loaded in both
   * modes without one clobbering the other.
   *
   * @param resource - Which suggestion set to load (e.g. `body_parts`, `workout`)
   * @param term - Optional search term; blank/whitespace returns all values
   * @param options - {@link SelectizeSearchOptions}; pass `{ compact: false }` for full data
   * @returns Array of suggestions whose shape matches the resource + compactness
   * @throws Error if the request fails
   */
  public async search<
    R extends SelectizeResource,
    Compact extends boolean = true,
  >(
    resource: R,
    term = '',
    options: SelectizeSearchOptions<Compact> = {}
  ): Promise<SelectizeSuggestionFor<R, Compact>[]> {
    const compact = options.compact ?? true;
    const trimmed = term.trim();
    // Suggestions are relative to the account (its friends, its plans), so
    // the account is in the key and another account misses.
    const key = `${resource}::${compact ? 'c' : 'f'}::${trimmed.toLowerCase()}::${syncGetAccountUsername() ?? '-'}`;

    // Return a fresh cached result without touching the network.
    const cached = this.cache.get(key);
    if (cached && dayjs().valueOf() - cached.timestamp < this.cacheTtlMs) {
      return [...cached.data] as SelectizeSuggestionFor<R, Compact>[];
    }

    // Coalesce identical concurrent requests onto a single promise.
    const pending = this.inFlight.get(key);
    if (pending) {
      return pending as Promise<SelectizeSuggestionFor<R, Compact>[]>;
    }

    const request = this.fetch(resource, trimmed, compact)
      .then((data) => {
        this.writeCache(key, data);
        return data;
      })
      .finally(() => {
        this.inFlight.delete(key);
      });

    this.inFlight.set(key, request);
    return request as Promise<SelectizeSuggestionFor<R, Compact>[]>;
  }

  /**
   * Clear all cached suggestions. Call after a mutation that could change the
   * available values so the next focus re-fetches fresh data.
   */
  public clearCache(): void {
    this.cache.clear();
  }

  /**
   * Drop everything, including the in-flight map, so a request the previous
   * account started cannot be joined by the next. What the session boundary
   * calls.
   */
  public clearAll(): void {
    this.cache.clear();
    this.inFlight.clear();
  }

  // ---------------------------------------------------------------------------
  // INTERNAL
  // ---------------------------------------------------------------------------

  private async fetch(
    resource: SelectizeResource,
    trimmedTerm: string,
    compact: boolean
  ): Promise<SelectizeSuggestion[]> {
    const params: QueryParams = compact ? { compact: 1 } : {};
    if (trimmedTerm) {
      params.search = trimmedTerm;
    }

    try {
      const response = await apiClient.get<SelectizeResponse>(
        `${this.baseUrl}/${resource}`,
        { params }
      );

      return response.data.data;
    } catch (error) {
      console.error(
        `Failed to load selectize options for "${resource}":`,
        error
      );
      rethrowTyped(error, 'Unable to load suggestions. Please try again.');
    }
  }

  private writeCache(key: string, data: SelectizeSuggestion[]): void {
    // Evict the oldest entry (Map preserves insertion order) once full.
    if (this.cache.size >= MAX_CACHE_ENTRIES && !this.cache.has(key)) {
      const oldestKey = this.cache.keys().next().value;
      if (oldestKey !== undefined) {
        this.cache.delete(oldestKey);
      }
    }

    this.cache.set(key, { data, timestamp: dayjs().valueOf() });
  }
}

// =============================================================================
// SERVICE INSTANCE
// =============================================================================

/**
 * Default selectize service instance.
 */
export const selectizeService = new SelectizeService();

// =============================================================================
// SESSION SCOPE
// =============================================================================

registerSessionScoped('selectizeService', () => {
  selectizeService.clearAll();
});
